import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { DEPARTURE_FROM_PLATFORM, DROP_OFF_ONLY, NO_PICKUP_OR_DROP_OFF, PICK_UP_ONLY, SPECIAL_DATA_TEXTS } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { eSpecialMessagesEffects } from 'src/app/modules/route-planner/constants/special-messages.constants';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { SpecialMessage } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { StopMessagesForDayResponse, StopTimesWithRoute } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';

@Component({
    selector: 'app-search-station-detailed-result-routes-calendar-list-item',
    templateUrl: './search-station-detailed-result-routes-calendar-list-item.component.html',
    styleUrls: ['./search-station-detailed-result-routes-calendar-list-item.component.scss'],
    standalone: false
})
export class SearchStationDetailedResultRoutesCalendarListItemComponent implements OnInit {
  @Input() stopTime: StopTimesWithRoute
  @Input() lines: Route[]
  @Input() routeSpecialMessages: StopMessagesForDayResponse['routeSpecialMessages'] = {};
  @Input() stopCode: number;
  @Input() isToday: boolean
  @Input() selectedDate: Date | null = null;
  @Input() selectedTime: string | null = null;
  @Output() pickLineEvent = new EventEmitter<Route>();

  relevantLine: Route

  onlyDropOffText: string
  pickUpOnlyText: string
  noPickupOrDropOffText: string
  departureFromPlatformText: string
  specialMessagesText: string

  constructor(
    private readonly modalService: ModalService,
    private readonly languageService: LanguageService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.relevantLine = this.lines.find(line => line.routeId === this.stopTime.routeId)
    this.initializeMultiLanguageTexts()
  }

  initializeMultiLanguageTexts() {
      this.languageService.selectedLanguage$.subscribe((language) => {
       this.onlyDropOffText = DROP_OFF_ONLY[language.language]
       this.pickUpOnlyText = PICK_UP_ONLY[language.language]
       this.noPickupOrDropOffText = NO_PICKUP_OR_DROP_OFF[language.language]
       this.departureFromPlatformText = DEPARTURE_FROM_PLATFORM[language.language]
       this.specialMessagesText = SPECIAL_DATA_TEXTS.specialMessges[language.language]
      });
    }

  /** Messages for this route that are in effect at the time this stop time departs. */
  get relevantRouteMessages(): SpecialMessage[] {
    const key = String(this.stopTime.routeId);
    const messages = this.routeSpecialMessages[key] ?? [];
    const departureDateTime = this.getDepartureDateTime();
    return messages.filter((msg) => this.isMessageActive(msg, departureDateTime));
  }

  private getDepartureDateTime(): Date {
    if (this.selectedDate && this.stopTime?.departureTimeToShow) {
      const d = new Date(this.selectedDate);
      const [hours, minutes, seconds = 0] = this.stopTime.departureTimeToShow.split(':').map(Number);
      d.setHours(hours, minutes, seconds, 0);
      return d;
    }
    return new Date();
  }

  openSpecialMessages(messages: SpecialMessage[]) {
    this.modalService.openSpecialMessagesModal(messages);
  }

  get lineSearchUrl(): string {
    if (!this.relevantLine || !this.stopCode) {
      return '#';
    }
    const lastStop = this.relevantLine.stops?.[this.relevantLine.stops.length - 1];
    return this.router.serializeUrl(
      this.router.createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          tab: eTab.LINES,
          routeId: this.relevantLine.routeId,
          oppositeRouteId: this.relevantLine.oppositeDirectionId,
          line: this.relevantLine.routeName,
          boardingStation: this.stopCode,
          dropingStation: lastStop?.stopcode,
          saveParams: true,
        },
      })
    );
  }

  onLineClick(line: Route) {
    this.pickLineEvent.emit(line);
  }

  onLineLinkClick(event: MouseEvent, line: Route): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    this.onLineClick(line);
  }

  onAgencyClick(agencyId: number) {
    this.modalService.openAgencyContactInfoModal(agencyId, false)
  }

  /** For screen readers: describe line and departure time. */
  get lineButtonAriaLabel(): string {
    if (!this.relevantLine || !this.stopTime) return '';
    const line = this.relevantLine.routeName || String(this.relevantLine.routeId);
    const time = this.stopTime.departureTimeToShow || '';
    return `${line} ${time}`.trim();
  }

  isRouteCanceled(): boolean {
    const messagesToCheck = this.relevantRouteMessages
    if (messagesToCheck.length === 0) {
      return false;
    }
    const checkDate = this.getDepartureDateTime();
    return messagesToCheck.some((message: SpecialMessage) => {
      const hasCancelEffect =
        message.effect.id === eSpecialMessagesEffects.NO_SERVICE ||
        message.effect.id === eSpecialMessagesEffects.DETOUR ||
        message.effect.id === eSpecialMessagesEffects.STOP_MOVED;
      if (!hasCancelEffect) {
        return false;
      }
      if (!this.isMessageActive(message, checkDate)) {
        return false;
      }
      return this.doesMessageAffectStop(message);
    });
  }

  private doesMessageAffectStop(message: SpecialMessage): boolean {
    if (!message.allAffectedStops?.length) {
      return true;
    }
    return message.allAffectedStops.some(
      (stop) => stop.stationCode === this.stopCode
    );
  }

  private isMessageActive(message: SpecialMessage, checkDate: Date): boolean {
    const fromDate = new Date(message.fromDate);
    const toDate = new Date(message.toDate);
    // Use actual from/to datetime from the API (e.g. "until 16.2 1AM" means toDate is that time, not end of day)
    return checkDate.getTime() >= fromDate.getTime() && checkDate.getTime() <= toDate.getTime();
  }

  getStopTimeTripHeadSign(): string {
    return this.stopTime.tripHeadsign[this.languageService.getSelectedLanguage().symbol] || this.stopTime.tripHeadsign.he || ""
  }
}
